import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  Loader2, Plus, CalendarClock, Pause, Play, X, Trash2, Zap,
  User, Building2, Clock, StickyNote,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  listStandingOrders, createStandingOrder,
  pauseStandingOrder, resumeStandingOrder, endStandingOrder, deleteStandingOrder,
  materializeStandingOrderNow, listFacilityContractPatients,
} from "@/lib/api";

const WEEKDAYS = [
  { id: "monday",    label: "Mon" }, { id: "tuesday",  label: "Tue" }, { id: "wednesday", label: "Wed" },
  { id: "thursday",  label: "Thu" }, { id: "friday",   label: "Fri" },
  { id: "saturday",  label: "Sat" }, { id: "sunday",   label: "Sun" },
];

const STATUS_META = {
  active: { label: "Active",  bg: "rgba(15,109,93,0.10)",   fg: "#0F6D5D", border: "rgba(15,109,93,0.3)" },
  paused: { label: "Paused",  bg: "rgba(148,163,184,0.16)", fg: "#475569", border: "#CBD5E1" },
  ended:  { label: "Ended",   bg: "rgba(220,38,38,0.10)",   fg: "#991B1B", border: "rgba(220,38,38,0.3)" },
};

const fmtDate = (v) => { if (!v) return "—"; try { return format(new Date(v), "PP"); } catch { return v; } };

/**
 * Standing Orders panel.
 * Props:
 *   - facilityId (required for creating orders)
 *   - patientId  (optional — if set, scopes the list & pre-fills the patient on create)
 *   - facilityName / patientName (for display)
 *   - showCreateButton (default true)
 */
export default function StandingOrdersPanel({
  facilityId, patientId, facilityName, patientName, showCreateButton = true, onChanged,
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (patientId) params.patient_id = patientId;
      else if (facilityId) params.facility_id = facilityId;
      const res = await listStandingOrders(params);
      setOrders(res.orders || []);
    } catch { toast.error("Failed to load standing orders."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [facilityId, patientId]);

  const runAction = async (fn, id, msg) => {
    try { await fn(id); await fetchAll(); onChanged?.(); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };

  return (
    <div className="space-y-3" data-testid="standing-orders-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "#0B2545" }}>
          {patientId ? "This patient" : "Facility"} · {orders.length} order{orders.length === 1 ? "" : "s"}
        </div>
        {showCreateButton && facilityId ? (
          <Button size="sm" onClick={() => setShowCreate(true)} className="h-8 text-xs"
                  style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="standing-order-create-button">
            <Plus className="h-3.5 w-3.5 mr-1" /> New Standing Order
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className="grid place-items-center py-10">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed p-6 text-center text-sm"
             style={{ borderColor: "#E2E8F0", color: "#64748B" }} data-testid="standing-orders-empty">
          No standing orders yet.
          {showCreateButton && facilityId ? " Create one to schedule recurring visits automatically." : ""}
        </div>
      ) : (
        <div className="space-y-2" data-testid="standing-orders-list">
          {orders.map((o) => <StandingOrderCard key={o.id} order={o} onAction={runAction} />)}
        </div>
      )}

      {showCreate && (
        <StandingOrderCreateDialog
          open onClose={() => setShowCreate(false)}
          facilityId={facilityId} facilityName={facilityName}
          initialPatientId={patientId} initialPatientName={patientName}
          onCreated={(_o) => { setShowCreate(false); fetchAll(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
const StandingOrderCard = ({ order, onAction }) => {
  const meta = STATUS_META[order.status] || STATUS_META.active;
  return (
    <div className="rounded-xl border p-3.5 bg-white"
         style={{ borderColor: "#E2E8F0" }}
         data-testid={`standing-order-card-${order.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-sm" style={{ color: "#0B2545" }}>
              {order.service_type}
            </div>
            <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border"
                  style={{ background: meta.bg, color: meta.fg, borderColor: meta.border }}
                  data-testid={`standing-order-status-${order.id}`}>
              {meta.label}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs" style={{ color: "#475569" }}>
            <span className="inline-flex items-center gap-1">
              <CalendarClock className="h-3 w-3" />
              Every {(order.day_of_week || "").replace(/^./, (c) => c.toUpperCase())} · {order.time_hhmm}
              {" · "}{order.frequency}
            </span>
            {order.assigned_staff_name ? (
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" /> {order.assigned_staff_name}
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]" style={{ color: "#64748B" }}>
            {order.patient_name ? (
              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {order.patient_name}</span>
            ) : (
              <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {order.facility_name}</span>
            )}
            <span>Start {fmtDate(order.start_date)}</span>
            {order.end_date ? <span>· End {fmtDate(order.end_date)}</span> : <span>· Ongoing</span>}
            <span>· Billed to facility contract</span>
          </div>
          {order.notes ? (
            <div className="mt-1.5 text-[11px] flex items-start gap-1" style={{ color: "#7A5E12" }}>
              <StickyNote className="h-3 w-3 mt-0.5 shrink-0" /> {order.notes}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 shrink-0">
          {order.status === "active" ? (
            <Button size="sm" variant="outline" onClick={() => onAction(pauseStandingOrder, order.id, "Order paused.")}
                    className="h-7 text-[10px] px-2" data-testid={`standing-order-pause-${order.id}`}>
              <Pause className="h-3 w-3 mr-1" /> Pause
            </Button>
          ) : order.status === "paused" ? (
            <Button size="sm" variant="outline" onClick={() => onAction(resumeStandingOrder, order.id, "Order resumed.")}
                    className="h-7 text-[10px] px-2" data-testid={`standing-order-resume-${order.id}`}>
              <Play className="h-3 w-3 mr-1" /> Resume
            </Button>
          ) : null}
          {order.status !== "ended" ? (
            <Button size="sm" variant="outline" onClick={() => onAction(endStandingOrder, order.id, "Order ended.")}
                    className="h-7 text-[10px] px-2" data-testid={`standing-order-end-${order.id}`}>
              <X className="h-3 w-3 mr-1" /> End
            </Button>
          ) : null}
          <Button size="sm" variant="outline"
                  onClick={() => onAction(materializeStandingOrderNow, order.id, "Materialized upcoming appointments.")}
                  className="h-7 text-[10px] px-2" data-testid={`standing-order-materialize-${order.id}`}>
            <Zap className="h-3 w-3 mr-1" /> Sync
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => {
                    if (!window.confirm("Delete this order? Future appointments will be purged.")) return;
                    onAction(deleteStandingOrder, order.id, "Order deleted.");
                  }}
                  className="h-7 text-[10px] px-2" style={{ color: "#B91C1C" }}
                  data-testid={`standing-order-delete-${order.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const StandingOrderCreateDialog = ({
  open, onClose, facilityId, facilityName, initialPatientId, initialPatientName, onCreated,
}) => {
  const [patients, setPatients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    patient_id: initialPatientId || "",
    service_type: "",
    frequency: "weekly",
    day_of_week: "monday",
    time_hhmm: "09:00",
    duration_min: 60,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    assigned_staff_name: "",
    notes: "",
  });

  useEffect(() => {
    if (!open || !facilityId) return;
    listFacilityContractPatients(facilityId)
      .then((r) => setPatients(r.patients || []))
      .catch(() => setPatients([]));
  }, [open, facilityId]);

  const submit = async () => {
    if (!form.service_type.trim()) { setError("Service is required."); return; }
    setSaving(true); setError("");
    try {
      const body = {
        facility_id: facilityId,
        patient_id: form.patient_id || null,
        service_type: form.service_type.trim(),
        frequency: form.frequency,
        day_of_week: form.day_of_week,
        time_hhmm: form.time_hhmm,
        duration_min: Number(form.duration_min) || 60,
        start_date: form.start_date,
        end_date: form.end_date || null,
        assigned_staff_name: form.assigned_staff_name || null,
        notes: form.notes || null,
      };
      const created = await createStandingOrder(body);
      toast.success(`Standing order created · ${created.service_type}`);
      onCreated?.(created);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-hidden flex flex-col p-0"
                     data-testid="standing-order-create-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b" style={{ borderColor: "#F1F5F9" }}>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" style={{ color: "#0F6D5D" }} />
            New Standing Order
          </DialogTitle>
          <DialogDescription>
            Recurring service for {facilityName || "this facility"}. Appointments auto-generate
            in Dispatch Center and Calendar for the next 6 weeks (extends nightly).
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {error ? (
            <div className="rounded-lg border p-3 text-xs"
                 style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
                 data-testid="standing-order-create-error">
              {error}
            </div>
          ) : null}

          <div>
            <Label className="text-xs">Contract Patient (optional)</Label>
            <Select value={form.patient_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, patient_id: v === "__all__" ? "" : v }))}>
              <SelectTrigger className="mt-1" data-testid="standing-order-patient_id">
                <SelectValue placeholder="Facility-wide (no specific patient)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Facility-wide (no specific patient)</SelectItem>
                {patients.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {initialPatientName ? (
              <p className="text-[10px] mt-1" style={{ color: "#64748B" }}>Pre-selected: {initialPatientName}</p>
            ) : null}
          </div>

          <div>
            <Label className="text-xs">Service *</Label>
            <Input className="mt-1" value={form.service_type} placeholder="Wound Care, CBC Draw, Phlebotomy…"
                   onChange={(e) => setForm((f) => ({ ...f, service_type: e.target.value }))}
                   data-testid="standing-order-service_type" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                <SelectTrigger className="mt-1" data-testid="standing-order-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                  <SelectItem value="monthly">Monthly (every 4 weeks)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Day of week</Label>
              <Select value={form.day_of_week} onValueChange={(v) => setForm((f) => ({ ...f, day_of_week: v }))}>
                <SelectTrigger className="mt-1" data-testid="standing-order-day_of_week"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.id[0].toUpperCase() + d.id.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Time (24h HH:MM)</Label>
              <Input className="mt-1" value={form.time_hhmm} placeholder="09:00"
                     onChange={(e) => setForm((f) => ({ ...f, time_hhmm: e.target.value }))}
                     data-testid="standing-order-time_hhmm" />
            </div>
            <div>
              <Label className="text-xs">Duration (min)</Label>
              <Input type="number" className="mt-1" value={form.duration_min} min={15} max={480}
                     onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))}
                     data-testid="standing-order-duration_min" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start date</Label>
              <Input type="date" className="mt-1" value={form.start_date}
                     onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                     data-testid="standing-order-start_date" />
            </div>
            <div>
              <Label className="text-xs">End date (optional)</Label>
              <Input type="date" className="mt-1" value={form.end_date}
                     onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                     data-testid="standing-order-end_date" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Assigned staff (name)</Label>
            <Input className="mt-1" value={form.assigned_staff_name} placeholder="Jazmin Negron"
                   onChange={(e) => setForm((f) => ({ ...f, assigned_staff_name: e.target.value }))}
                   data-testid="standing-order-assigned_staff_name" />
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} className="mt-1" value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Fall risk. Bring extra dressings."
                      data-testid="standing-order-notes" />
          </div>

          <div className="rounded-lg border p-2.5 text-[11px]"
               style={{ borderColor: "rgba(15,109,93,0.28)", background: "rgba(15,109,93,0.06)", color: "#0F6D5D" }}>
            <div className="font-semibold mb-0.5">Billing routed through facility contract</div>
            Patients are not billed. Every appointment auto-attaches to this facility for monthly invoicing.
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t" style={{ borderColor: "#F1F5F9" }}>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.service_type}
                  className="h-9" style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="standing-order-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
            Create Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
